// src/features/groups/services/groupService.js
import { auth, db, storage, functions } from '../../../firebase';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

/**
 * Subscribe to a user's inbox summary docs for the group list.
 * Keeps caching/state ownership at the caller level (e.g., context).
 * @param {string} userId - Authenticated user ID
 * @param {Function} onUpdate - Callback with (groups[], { fromCache, size })
 * @param {Function} onError - Callback for errors
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserGroups = (userId, onUpdate, onError) => {
  if (!userId) {
    onUpdate([], { fromCache: false, size: 0 });
    return () => {};
  }

  const userGroupsRef = collection(db, 'users', userId, 'groups');
  const q = query(userGroupsRef, orderBy('lastActivityAt', 'desc'));

  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => {
      const groups = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        lastReadAt: docSnap.data().lastReadAt?.toDate?.() || new Date(0),
      }));
      onUpdate(groups, {
        fromCache: snapshot.metadata.fromCache,
        size: snapshot.size,
      });
    },
    (error) => {
      console.error('Error subscribing to user groups:', error);
      if (onError) onError(error);
    }
  );
};

/**
 * Subscribe to unread group count for the specified user
 * @param {string} userId - Authenticated user ID
 * @param {Function} onUpdate - Callback with the unread count
 * @param {Function} onError - Callback for errors
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUnreadGroupCount = (userId, onUpdate, onError) => {
  if (!userId) {
    onUpdate(0);
    return () => {};
  }

  // Query inbox summary docs directly (single source of truth for the list)
  const q = query(
    collection(db, 'users', userId, 'groups'),
    where('unreadCount', '>', 0)
  );

  return onSnapshot(
    q,
    (querySnapshot) => {
      onUpdate(querySnapshot.size);
    },
    (error) => {
      console.error('Error fetching unread group count:', error);
      if (onError) onError(error);
      onUpdate(0);
    }
  );
};

/**
 * Subscribe to a specific group.
 * @param {string} groupId - Group ID
 * @param {Function} onUpdate - Callback for group data updates
 * @param {Function} onError - Callback for errors
 * @returns {Function} Unsubscribe function
 */
export const subscribeToGroup = (groupId, onUpdate, onError) => {
  if (!groupId || !auth.currentUser) {
    return () => {};
  }

  try {
    const groupRef = doc(db, 'groups', groupId);
    return onSnapshot(
      groupRef,
      (snapshot) => {
        if (snapshot.exists()) {
          onUpdate({
            id: snapshot.id,
            ...snapshot.data(),
          });
        }
      },
      (error) => {
        console.error('Error subscribing to group:', error);
        if (onError) onError(error);
      }
    );
  } catch (error) {
    console.error('Error setting up group subscription:', error);
    if (onError) onError(error);
    return () => {};
  }
};

/**
 * Subscribe to member data for a group.
 * @param {string} groupId - Group ID
 * @param {Function} onUpdate - Callback for member data updates
 * @param {Function} onError - Callback for errors
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMembership = (groupId, onUpdate, onError) => {
  const user = auth.currentUser;
  if (!groupId || !user) {
    return () => {};
  }

  try {
    const memberRef = doc(db, `groups/${groupId}/members/${user.uid}`);
    return onSnapshot(
      memberRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const memberData = docSnap.data();
          onUpdate({
            role: memberData.role,
            joinedAt: memberData.joinedAt ? memberData.joinedAt.toDate() : null,
            notificationsEnabled: memberData.notificationsEnabled !== false,
            botResponsesEnabled: memberData.botResponsesEnabled !== false,
          });
        } else {
          if (onError) onError(new Error('Not a member of this group'));
        }
      },
      (error) => {
        console.error('Error subscribing to member data:', error);
        if (onError) onError(error);
      }
    );
  } catch (error) {
    console.error('Error setting up membership subscription:', error);
    if (onError) onError(error);
    return () => {};
  }
};

/**
 * Toggle bot response settings for a group.
 * @param {string} groupId - Group ID
 * @param {boolean} enabled - Whether bot responses should be enabled
 * @returns {Promise<boolean>} Success status
 */
export const toggleGroupBotResponses = async (groupId, enabled) => {
  const user = auth.currentUser;
  if (!user) return false;

  try {
    const memberRef = doc(db, `groups/${groupId}/members/${user.uid}`);
    await updateDoc(memberRef, {
      botResponsesEnabled: enabled,
    });
    return true;
  } catch (error) {
    console.error('Error toggling bot responses:', error);
    return false;
  }
};

/**
 * Reset unread count for a user in a group.
 * Advances the member read cursor and clears unreadCount in the inbox summary doc.
 * @param {string} groupId - The group ID
 * @returns {Promise<boolean>} Success status
 */
export const resetUnreadCount = async (groupId) => {
  if (!auth.currentUser) return false;

  try {
    const userId = auth.currentUser.uid;
    const batch = writeBatch(db);
    const now = serverTimestamp();

    // Update member doc (source of truth)
    const memberRef = doc(db, `groups/${groupId}/members/${userId}`);
    batch.update(memberRef, {
      lastReadAt: now
    });

    // Also update inbox summary doc for instant UI update
    const inboxRef = doc(db, `users/${userId}/groups/${groupId}`);
    batch.update(inboxRef, {
      unreadCount: 0,
      lastReadAt: now,
      updatedAt: now
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error resetting unread count:', error);
    return false;
  }
};

/**
 * Upload a group icon to storage.
 * @param {File} iconFile - The icon file to upload
 * @param {string} groupId - The group ID (used in the storage path)
 * @returns {Promise<string|null>} The download URL or null if no file
 */
const uploadGroupIcon = async (iconFile, groupId) => {
  if (!iconFile) return null;

  const iconRef = ref(storage, `group-icons/${groupId}/${Date.now()}-${iconFile.name}`);
  await uploadBytes(iconRef, iconFile);
  return getDownloadURL(iconRef);
};

/**
 * Create a new group using server-side callable function.
 * The server atomically creates the group doc, member docs, and user index docs.
 *
 * @param {string} name - Group name
 * @param {File} iconFile - Group icon file (optional)
 * @param {Array} memberIds - Member IDs to add to group (excluding creator)
 * @returns {Promise<Object>} Newly created group
 * @throws {Error} If group creation fails
 */
export const createGroup = async (name, iconFile, memberIds) => {
  if (!name?.trim() && (!memberIds || memberIds.length === 0)) {
    throw new Error('Group name or members are required');
  }

  try {
    const userId = auth.currentUser.uid;

    // Call the server-side function to create the group
    const callable = httpsCallable(functions, 'groupsCreate');
    const result = await callable({
      name: name?.trim() || 'New Group',
      memberIds: memberIds || [],
      icon: null, // Icon uploaded after group creation
    });

    const { groupId } = result.data;

    // Upload the icon if provided and update the group
    let iconUrl = null;
    if (iconFile && groupId) {
      iconUrl = await uploadGroupIcon(iconFile, groupId);
      if (iconUrl) {
        const groupRef = doc(db, 'groups', groupId);
        await updateDoc(groupRef, { icon: iconUrl });
      }
    }

    // Fetch the created group to return complete data
    const groupDoc = await getDoc(doc(db, 'groups', groupId));
    const groupData = groupDoc.exists() ? groupDoc.data() : {};

    return {
      id: groupId,
      ...groupData,
      icon: iconUrl || groupData.icon,
      createdBy: userId,
      createdAt: new Date(),
      lastActivityAt: new Date(),
    };
  } catch (error) {
    console.error('Error creating group:', error);
    throw error;
  }
};

/**
 * Get a group by ID
 * @param {string} groupId - Group ID
 * @returns {Promise<Object>} Group data
 * @throws {Error} If group cannot be found
 */
export const getGroupById = async (groupId) => {
  try {
    const groupDoc = await getDoc(doc(db, 'groups', groupId));
    
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    return {
      id: groupDoc.id,
      ...groupDoc.data()
    };
  } catch (error) {
    console.error('Error loading group:', error);
    throw error;
  }
};

/**
 * Get group members
 * @param {string} groupId - Group ID
 * @returns {Promise<Array>} Group members with user data
 * @throws {Error} If members cannot be fetched
 */
export const getGroupMembers = async (groupId) => {
  try {
    const membersRef = collection(db, `groups/${groupId}/members`);
    const membersSnap = await getDocs(membersRef);
    
    const memberPromises = membersSnap.docs.map(async (memberDoc) => {
      const memberData = memberDoc.data();
      const userId = memberDoc.id;
      
      // Get user data
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      
      return {
        id: userId,
        userId: userId,
        ...memberData,
        displayName: userData.displayName || 'Anonymous',
        profilePictureUrl: userData.profilePictureUrl
      };
    });
    
    return Promise.all(memberPromises);
  } catch (error) {
    console.error('Error loading members:', error);
    throw error;
  }
};

/**
 * Update group settings
 * @param {string} groupId - Group ID
 * @param {Object} groupData - Group data to update
 * @param {File} iconFile - New group icon (optional)
 * @returns {Promise<Object>} Updated group data
 * @throws {Error} If update fails
 */
export const updateGroupSettings = async (groupId, groupData, iconFile) => {
  try {
    let iconUrl = groupData.icon ?? null;

    // Upload new icon if provided
    if (iconFile) {
      const iconRef = ref(storage, `group-icons/${groupId}/${Date.now()}-${iconFile.name}`);
      await uploadBytes(iconRef, iconFile);
      iconUrl = await getDownloadURL(iconRef);
    }

    // Explicit allowlist to avoid sending server-managed fields
    const updates = {
      name: groupData.name?.trim(),
      icon: iconUrl,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, 'groups', groupId), updates);

    // Return authoritative data (including server timestamps and managed fields)
    const updatedSnap = await getDoc(doc(db, 'groups', groupId));
    return {
      id: updatedSnap.id,
      ...updatedSnap.data()
    };
  } catch (error) {
    console.error('Error updating group:', error);
    throw error;
  }
};

/**
 * Set notification preference for the current user in a group.
 * Canonical data lives on the membership doc; the inbox summary copy is a projection.
 * @param {string} groupId - Group ID
 * @param {boolean} enabled - Whether notifications should be enabled
 * @returns {Promise<boolean>} Success status
 * @throws {Error} If toggle fails or user is unauthenticated
 */
export const setMyGroupNotificationsEnabled = async (groupId, enabled) => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    throw new Error('User not authenticated');
  }
  if (!groupId) {
    throw new Error('Group ID is required');
  }

  try {
    const batch = writeBatch(db);
    const now = serverTimestamp();

    // Source of truth: membership doc
    const memberRef = doc(db, `groups/${groupId}/members/${userId}`);
    batch.update(memberRef, {
      notificationsEnabled: enabled,
      updatedAt: now,
    });

    // Projection for inbox summary (created server-side); client updates only
    const inboxRef = doc(db, `users/${userId}/groups/${groupId}`);
    batch.update(inboxRef, {
      notificationsEnabled: enabled,
      updatedAt: now,
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error toggling notifications:', error);
    throw error;
  }
};

/**
 * Remove a member from a group using server-side callable function.
 * Only admins can remove other members. The server enforces last-admin protection.
 *
 * @param {string} groupId - Group ID
 * @param {string} memberId - Member ID to remove
 * @returns {Promise<void>}
 * @throws {Error} If member removal fails
 */
export const removeGroupMember = async (groupId, memberId) => {
  try {
    const callable = httpsCallable(functions, 'groupsRemoveMember');
    await callable({ groupId, memberId });
  } catch (error) {
    console.error('Error removing member:', error);
    throw error;
  }
};

/**
 * Leave a group (current user) using server-side callable function.
 * The server enforces last-admin protection.
 *
 * @param {string} groupId - Group ID
 * @returns {Promise<void>}
 * @throws {Error} If leaving fails
 */
export const leaveGroup = async (groupId) => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    throw new Error('User not authenticated');
  }

  try {
    const callable = httpsCallable(functions, 'groupsRemoveMember');
    await callable({ groupId, memberId: userId });
  } catch (error) {
    console.error('Error leaving group:', error);
    throw error;
  }
};

/**
 * Add members to a group using server-side callable function.
 * Only admins can add members. The server handles idempotency.
 *
 * @param {string} groupId - Group ID
 * @param {Array} memberIds - Array of user IDs to add as members
 * @returns {Promise<{ addedMembers: Array, addedCount: number }>} Added members with user data
 * @throws {Error} If members cannot be added
 */
export const addGroupMembers = async (groupId, memberIds) => {
  if (!groupId || !memberIds || memberIds.length === 0) {
    throw new Error('Invalid group or no members to add');
  }

  try {
    const callable = httpsCallable(functions, 'groupsAddMembers');
    const result = await callable({ groupId, memberIds });

    const addedUids = Array.isArray(result?.data?.addedUids) ? result.data.addedUids : [];
    const addedCount = result?.data?.addedCount ?? addedUids.length ?? 0;

    if (!addedCount || addedUids.length === 0) {
      return { addedMembers: [], addedCount };
    }

    // Only build member objects for users the server actually added
    const addedMembers = await Promise.all(
      addedUids.map(async (userId) => {
        const [memberSnap, userSnap] = await Promise.all([
          getDoc(doc(db, `groups/${groupId}/members/${userId}`)),
          getDoc(doc(db, 'users', userId)),
        ]);

        const memberData = memberSnap.exists() ? memberSnap.data() : {};
        const userData = userSnap.exists() ? userSnap.data() : {};

        return {
          id: userId,
          userId,
          ...memberData,
          joinedAt: memberData.joinedAt?.toDate?.() || memberData.joinedAt || new Date(),
          lastReadAt: memberData.lastReadAt?.toDate?.() || memberData.lastReadAt || new Date(),
          displayName: userData.displayName || 'Anonymous',
          profilePictureUrl: userData.profilePictureUrl ?? memberData.profilePictureUrl,
        };
      })
    );

    return { addedMembers, addedCount };
  } catch (error) {
    console.error('Error adding members:', error);
    throw error;
  }
};

/**
 * Get available friends for a group (friends who aren't members)
 * @param {Array} friends - User's friends list
 * @param {Array} existingMembers - Current group members
 * @returns {Array} Friends who aren't members
 */
export const getAvailableFriendsForGroup = (friends, existingMembers) => {
  if (!friends || !existingMembers) return [];
  
  const existingMemberIds = existingMembers.map(member => member.userId);
  return friends.filter(friend => !existingMemberIds.includes(friend.id));
};

/**
 * Format relative time for a group message
 * @param {Date} date - Date to format
 * @returns {string} Formatted time
 */
export const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return 'Just now';
};

/**
 * Fetch and verify post IDs associated with a specific group.
 * Gets post IDs from the group's 'posts' subcollection, ordered by creation time,
 * and verifies that each corresponding post document exists in the main 'posts' collection.
 * Legacy posts feature: retained for backward compatibility with existing production data; remove once Records fully replace posts.
 * 
 * @param {string} groupId - The ID of the group.
 * @returns {Promise<string[]>} A promise that resolves to an array of verified post IDs.
 * @throws {Error} If fetching or verification fails.
 */
export const getVerifiedGroupPostIds = async (groupId) => {
  if (!groupId) {
    throw new Error("Group ID is required to fetch posts.");
  }

  try {
    // 1. Get post references from the group's posts subcollection
    const groupPostsRef = collection(db, `groups/${groupId}/posts`);
    const q = query(groupPostsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    // 2. Verify each post exists in the main 'posts' collection
    const postPromises = querySnapshot.docs.map(async (docSnap) => {
      const postId = docSnap.data().postId;
      if (!postId) return null; // Skip if postId is missing for some reason

      const postRef = doc(db, 'posts', postId);
      const postSnap = await getDoc(postRef);
      
      // Only return the ID if the post document actually exists
      return postSnap.exists() ? postId : null;
    });

    // 3. Wait for all verifications and filter out nulls (non-existent posts)
    const verifiedIds = (await Promise.all(postPromises)).filter(id => id !== null);
    return verifiedIds;

  } catch (error) {
    console.error(`Error fetching verified posts for group ${groupId}:`, error);
    // Re-throw the error to be caught by the calling hook/component
    throw new Error('Failed to load group posts. Please try again.'); 
  }
}; 
